import { useTheme } from '@emotion/react';
import type { Theme } from '@emotion/react';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { useMemo } from 'react';

type ComponentTheme = {
  [key: string]: Record<string, unknown>;
  default: Record<string, unknown>;
};

const cache = new WeakMap();

const getComponentThemeMemoized = (componentTheme: ComponentTheme, mode: string) => {
  const value = componentTheme[mode];

  if (cache.has(value)) {
    return cache.get(value);
  }

  const result = merge(cloneDeep(componentTheme.default), value);

  cache.set(value, result);

  return result;
};

export const useComponentTheme = (getComponentTheme: (theme: Theme) => ComponentTheme | undefined, mode?: string) => {
  const theme = useTheme();

  return useMemo(() => {
    const componentTheme = getComponentTheme(theme);

    if (!componentTheme) {
      return { mode };
    }

    if (!mode || !componentTheme[mode]) {
      return componentTheme.default;
    }

    return getComponentThemeMemoized(componentTheme, mode);
  }, [getComponentTheme, mode, theme]);
};
